import { Handle, NodeProps, Position } from "@xyflow/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faExclamationTriangle, faLightbulb } from "@fortawesome/pro-regular-svg-icons"; // Using regular icons

import { useTranslation } from "react-i18next";

import { useMemo, useState } from "react";

import { ZodIssue } from "zod";

import { PhaseContentPreview } from "./PhaseContentPreview";
import { PhaseNodeData } from "./NodeDataTyps";
import { PhaseDataSchema } from "../../schema/courses";

export default function PhaseNode({ data, selected, ...nodeData }: NodeProps<PhaseNodeData>) {
	// Define base and selected styles for clarity and easier modification
	const baseStyle = "bg-white border-border shadow-sm hover:shadow-md";
	const selectedStyle = "bg-white border-accent ring-2 ring-accent shadow-lg opacity-100";

	const { t } = useTranslation();
	const ts = (key: string) => t(`phaseNode.${key}`);
	const te = (key: string) => t(`editor.${key}`);
	const tv = (key: string) => t(`phaseNode.validationError.${key}`);
	const [showValidationTooltip, setShowValidationTooltip] = useState(false);

	const hasSpoilerContent = !!data.spoilerContent;
	const hasDelayedContent = !!(data.timeDelayedContent && data.timeDelayedContent.length > 0);

	const validationResult = useMemo(() => {
		const result = PhaseDataSchema.safeParse(data);

		if (result.success) {
			return { isValid: true, issues: [] as ZodIssue[] };
		}

		return { isValid: false, issues: result.error.issues };
	}, [data]);

	const isValidNode = validationResult.isValid;

	const mapIssueToMessage = (issue: ZodIssue): string => {
		const path = issue.path.join(".");

		if (path === "content") {
			return tv("contentRequired");
		}

		if (path.includes("decisionOptions") && path.includes("option")) {
			return tv("decisionOptionRequired");
		}

		if (path === "decisionName") {
			return tv("decisionNameRequired");
		}

		if (path === "timeLeftDecisionId") {
			return tv("timeLeftDecisionRequired");
		}

		if (path === "timeLimit") {
			return tv("timeLimitRequired");
		}

		if (path === "confirmationRequired") {
			return tv("confirmationRequired");
		}

		if (path === "phaseEndText") {
			return tv("phaseEndTextRequired");
		}

		if (path.includes("endTimeInSeconds")) {
			return tv("endTimeInvalid");
		}

		return t("phaseNode.validationError.generic", { path });
	};

	const firstNode = data.id === null ? false : data.id === 1;

	return (
		<div
			className={`
				w-60
				rounded-xl
				p-3
				text-slate-700
				relative
				transition-all duration-150 ease-in-out
				${selected ? selectedStyle : baseStyle}
				${!isValidNode ? "border border-red-500" : ""}
			`}
		>
			{!firstNode && (
				<Handle
					className={`!w-3.5 !h-3.5 !-top-1 !border-2 !border-white !bg-slate-300 hover:!bg-slate-400 ${
						selected ? "!bg-accent hover:!bg-accent-dark !ring-accent-tint" : "!ring-border"
					}`}
					position={Position.Top}
					type="target"
				/>
			)}
			{/* Node Header: Phase Name, ID, and new indicators */}
			<div className="flex items-start justify-between mb-1.5">
				<h1
					className="text-[13px] font-semibold text-slate-800 truncate flex-grow pr-1"
					title={data.phaseName || ""}
				>
					{data.phaseName || te("newPhase")}
				</h1>
				<div className="flex items-center flex-shrink-0">
					{!isValidNode && (
						<div
							className="relative"
							onMouseEnter={() => setShowValidationTooltip(true)}
							onMouseLeave={() => setShowValidationTooltip(false)}
						>
							<FontAwesomeIcon
								className="text-red-500 text-xs mr-1.5 cursor-help"
								icon={faExclamationTriangle}
							/>
							{showValidationTooltip && validationResult.issues.length > 0 && (
								<div className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-1 w-56 bg-white border border-red-200 rounded-lg shadow-lg p-2">
									<p className="text-[10px] font-semibold text-red-600 mb-1">{ts("invalidNode")}</p>
									<ul className="space-y-0.5">
										{validationResult.issues.map((issue, idx) => (
											<li key={idx} className="text-[10px] text-red-500 flex items-start gap-1">
												<span className="mt-0.5">•</span>
												<span>{mapIssueToMessage(issue)}</span>
											</li>
										))}
									</ul>
								</div>
							)}
						</div>
					)}
					{hasSpoilerContent && (
						<FontAwesomeIcon
							className="text-yellow-500 text-xs mr-1.5"
							icon={faLightbulb}
							title={ts("spoilerAvailable")}
						/>
					)}
					{hasDelayedContent && (
						<FontAwesomeIcon
							className="text-sky-500 text-xs mr-1.5"
							icon={faClock}
							title={ts("delayedContentAvailable")}
						/>
					)}
					<span
						className="ml-1 text-[10px] font-mono text-text bg-accent-tint px-1.5 py-0.5 rounded-btn"
						title={`Phase ID: ${data.id}`}
					>
						#{data.id}
					</span>
				</div>
			</div>

			{/* Node content: the media itself where there is any, the text otherwise. */}
			<PhaseContentPreview
				content={data.content}
				contentType={data.contentType}
				emptyLabel={te("newContent")}
			/>

			{/* <button onClick={() => data?.deleteNode?.(nodeData.id)}>delete</button> */}
			{/* Optional: Display number of decision options if present */}
			{data.decisionOptions && data.decisionOptions.length > 0 && (
				<div className="mt-2 pt-1.5 border-t border-border/60">
					<p className="text-[10px] text-subtle font-medium">
						{data.decisionOptions.length}{" "}
						{data.decisionOptions.length === 1 ? ts("decisionsOption") : ts("decisionsOptions")}
					</p>
				</div>
			)}
			{(!data.decisionOptions || data.decisionOptions.length === 0) && data.decisionName && (
				<div className="mt-2 pt-1.5 border-t border-border/60">
					<p> </p>
				</div>
			)}

			{/* Bottom Handle - customized appearance */}
			<Handle
				className={`!w-3.5 !h-3.5 !-bottom-1 !border-2 !border-white !bg-slate-300 hover:!bg-slate-400 ${
					selected ? "!bg-accent hover:!bg-accent-dark !ring-accent-tint" : "!ring-border"
				}`}
				position={Position.Bottom}
				type="source"
			/>
		</div>
	);
}
